/**
 * Level transition hooks for {@link BaseGameLoop.openLevel}.
 * DOM overlays use {@link TransitionContext.container} so effects persist while the world is torn down.
 */
import type { BaseGameLoop } from './GameLoop.js';
import type { AssetPath } from '../storage/storageProvider.js';

/** Context passed to {@link ISceneTransition} during {@link BaseGameLoop.openLevel}. */
export interface TransitionContext {
  gameLoop: BaseGameLoop;
  /** Game viewport container — attach DOM overlays here. */
  container: HTMLElement;
  targetScenePath: AssetPath;
}

/**
 * Controls timing when {@link BaseGameLoop.openLevel} replaces the active level.
 *
 * @remarks
 * {@link shouldSwitchScenes} must stay false until fade-out (or similar) finishes so the swap
 * happens under cover. {@link isFinished} must stay false until fade-in (or similar) completes.
 */
export interface ISceneTransition {
  onBegin?(ctx: TransitionContext): void | Promise<void>;
  /** `true` when the engine may destroy the world and load the next scene. */
  shouldSwitchScenes(ctx: TransitionContext): boolean;
  /** Called after the new level is loaded and {@link BaseGameLoop.enterPlayMode} has run. */
  onSceneLoaded?(ctx: TransitionContext): void | Promise<void>;
  /** `true` when the transition is fully complete. */
  isFinished(ctx: TransitionContext): boolean;
  update(ctx: TransitionContext, dt: number): void;
  dispose?(): void;
}

export interface FadeToBlackOptions {
  /** Seconds to fade out before swapping scenes. @defaultValue `0.35` */
  fadeOutDuration?: number;
  /** Seconds to fade in after the new scene loads. @defaultValue `0.35` */
  fadeInDuration?: number;
}

const FADE_OVERLAY_Z_INDEX = 100_000;

/**
 * Full-screen fade to black via a DOM overlay above the renderer canvas.
 */
export class FadeToBlackTransition implements ISceneTransition {
  private readonly fadeOutDuration: number;
  private readonly fadeInDuration: number;
  private phase: 'fadeOut' | 'ready' | 'fadeIn' | 'done' = 'fadeOut';
  private elapsed = 0;
  private overlay: HTMLElement | null = null;

  constructor(options: FadeToBlackOptions = {}) {
    this.fadeOutDuration = options.fadeOutDuration ?? 0.35;
    this.fadeInDuration = options.fadeInDuration ?? 0.35;
  }

  public onBegin(ctx: TransitionContext): void {
    const overlay = document.createElement('div');
    overlay.setAttribute('data-scene-transition', 'fade');
    overlay.style.cssText = [
      'position:absolute',
      'inset:0',
      'background:#000',
      'opacity:0',
      'pointer-events:none',
      `z-index:${FADE_OVERLAY_Z_INDEX}`,
    ].join(';');
    ctx.container.appendChild(overlay);
    this.overlay = overlay;
  }

  public shouldSwitchScenes(_ctx: TransitionContext): boolean {
    return this.phase !== 'fadeOut';
  }

  public onSceneLoaded(_ctx: TransitionContext): void {
    if (this.phase === 'ready') {
      this.phase = 'fadeIn';
      this.elapsed = 0;
    }
  }

  public isFinished(_ctx: TransitionContext): boolean {
    return this.phase === 'done';
  }

  public update(_ctx: TransitionContext, dt: number): void {
    if (this.phase === 'done' || !this.overlay) {
      return;
    }

    this.elapsed += dt;

    if (this.phase === 'fadeOut') {
      const t = this.fadeOutDuration > 0 ? Math.min(1, this.elapsed / this.fadeOutDuration) : 1;
      this.overlay.style.opacity = String(t);
      if (t >= 1) {
        this.phase = 'ready';
        this.elapsed = 0;
        this.overlay.style.opacity = '1';
      }
      return;
    }

    if (this.phase === 'ready') {
      this.overlay.style.opacity = '1';
      return;
    }

    if (this.phase === 'fadeIn') {
      const t = this.fadeInDuration > 0 ? Math.min(1, this.elapsed / this.fadeInDuration) : 1;
      this.overlay.style.opacity = String(1 - t);
      if (t >= 1) {
        this.phase = 'done';
        this.overlay.style.opacity = '0';
      }
    }
  }

  public dispose(): void {
    this.overlay?.remove();
    this.overlay = null;
  }
}
